
"use client";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AppNavigation from "@/components/navigation";
import { PackageSearch, CircleCheck, CircleX, Hourglass, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import type { TrackingStatus } from "@/lib/types";

export default function TrackingPage() {
  const [trackingId, setTrackingId] = useState("");
  const [status, setStatus] = useState<TrackingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingId) return;

    setIsLoading(true);
    setError(null);
    setStatus(null);

    try {
      const res = await fetch(`/api/tracking/${trackingId}`);
      if (!res.ok) {
        throw new Error("No se encontró el número de seguimiento.");
      }
      const data: TrackingStatus = await res.json();
      setStatus(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const statusIcons: Record<TrackingStatus['status'], React.ReactElement> = {
    Recibido: <CircleCheck className="h-5 w-5 text-green-500" />,
    "En diagnóstico": <Hourglass className="h-5 w-5 text-yellow-500" />,
    "Esperando refacción": <Hourglass className="h-5 w-5 text-yellow-500" />,
    "En reparación": <Hourglass className="h-5 w-5 text-orange-500" />,
    "Listo para entrega": <CircleCheck className="h-5 w-5 text-green-500" />,
    Entregado: <CircleCheck className="h-5 w-5 text-green-500" />,
    Cancelado: <CircleX className="h-5 w-5 text-red-500" />,
    Garantia: <ShieldCheck className="h-5 w-5 text-indigo-500" />,
  };

  const getProgressValue = (currentStatus: string) => {
    const order = ["Recibido", "En diagnóstico", "En reparación", "Listo para entrega", "Entregado"];
    const index = order.indexOf(currentStatus);
    if (index === -1) return 0;
    return ((index + 1) / order.length) * 100;
  }

  return (
    <main className="min-h-screen bg-background p-4 sm:p-8">
      <AppNavigation />
      <div className="max-w-2xl mx-auto mt-6">
        <header className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2 font-headline">
            Rastreo de Reparación
          </h1>
          <p className="text-lg text-muted-foreground">
            Ingresa tu número de seguimiento para ver el estado de tu
            reparación.
          </p>
        </header>
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-headline">
              <PackageSearch className="w-6 h-6 text-primary" />
              Buscar mi dispositivo
            </CardTitle>
            <CardDescription>
              Escribe el código que se te proporcionó al momento de dejar tu
              equipo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex items-center gap-4">
              <Input
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder="Número de seguimiento (ej. 0048C-1)"
                className="flex-grow"
                disabled={isLoading}
              />
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Buscando..." : "Rastrear"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive" className="mt-6">
            <CircleX className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {status && (
          <Card className="mt-6 shadow-lg">
            <CardHeader>
              <CardTitle>Detalles de la Reparación</CardTitle>
              <CardDescription>
                ID de seguimiento: {status.id}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold">{status.device_brand} {status.device_model}</p>
                <p className="text-sm text-muted-foreground">{status.repair_type}</p>
              </div>
              <div className="space-y-2">
                 <Progress value={getProgressValue(status.status)} className="w-full" />
                 <div className="flex items-center gap-2 font-medium">
                    {statusIcons[status.status as keyof typeof statusIcons]}
                    <span>{status.status}</span>
                 </div>
              </div>
              <p className="text-sm text-muted-foreground pt-2 border-t mt-4">{status.notes}</p>
            </CardContent>
          </Card>
        )}

        <footer className="text-center mt-12 py-6 border-t">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} RzCell. Todos los derechos
            reservados.
          </p>
        </footer>
      </div>
    </main>
  );
}
